import React, { useState, useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { confirmRegistration, checkTokenExpired } from "../../api/admin";
import {
  TextField,
  Box,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "./Styling.css";
import UserAlreadyExistsBox from "../Partials/UserAlreadyExistsBox";
import SuccessfulRegistrationBox from "../Partials/SuccessfulRegistrationBox";
import ExpiredLinkMessage from "../Partials/ExpiredLinkMessage";
import InvalidLinkMessage from "../Partials/InvalidLinkMessage";
import { s3BaseUrl } from "../../config/envConfig";

export default function RegisterPage2() {
  const history = useHistory();
  const { id, token, expires, signature } = useParams();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [formErrors, setFormErrors] = useState({
    username: false,
    password: false,
    confirmPassword: false,
  });
  const [formValid, setFormValid] = useState(false);
  const [passwordCriteriaMessage, setPasswordCriteriaMessage] = useState(false);
  const [passwordMatchMessage, setPasswordMatchMessage] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorBox, setShowErrorBox] = useState(false);
  const [showSuccessBox, setShowSuccessBox] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const [isValidLink, setIsValidLink] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);

  // Password validation criteria
  const passwordCriteria =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&#^.,;:'"<>{}[\]()/|`~+=_-]{8,}$/;

  const handleInputChange = (key, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const validateField = (key, value) => {
    let isValid = value.length !== 0;

    if (key === "password") {
      isValid = isValid && passwordCriteria.test(value);
      setPasswordCriteriaMessage(!passwordCriteria.test(value) && value !== "");
    }

    if (key === "confirmPassword") {
      isValid = isValid && value === formData.password;
      setPasswordMatchMessage(value !== formData.password && value !== "");
    }

    return isValid;
  };

  const handleBlur = (key, value) => {
    const isValid = validateField(key, value);
    setFormErrors((prevState) => ({
      ...prevState,
      [key]: !isValid,
    }));
  };

  const handleSubmit = async () => {
    const newFormErrors = {
      username: !validateField("username", formData.username),
      password: !validateField("password", formData.password),
      confirmPassword: !validateField(
        "confirmPassword",
        formData.confirmPassword
      ),
    };

    setFormErrors(newFormErrors);
    const isValid = Object.values(newFormErrors).every((error) => !error);

    if (isValid) {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const registrationResponse = await confirmRegistration(
          parseInt(id),
          token,
          formData.username,
          formData.password
        );
        setIsLoading(false);
        if (registrationResponse.code === 200) {
          setShowSuccessBox(true);
        } else {
          setShowErrorBox(true);
        }
      } catch (error) {
        setIsLoading(false);
        setShowErrorBox(true);
      }
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    if (!token) {
      setErrorMessage("Invalid registration link. The link is missing required information.");
      setIsValidLink(false);
      setIsCheckingToken(false);
      return;
    }

    const checkToken = async () => {
      setIsCheckingToken(true);
      try {
        const response = await checkTokenExpired(token);
        
        if (response.code === 200) {
          if (response.data.expired) {
            setErrorMessage("This registration link has expired. Please request a new one.");
            setIsTokenExpired(true);
            setIsValidLink(false);
          } else if (response.data.valid === false) {
            setErrorMessage("Invalid registration link. This link is not recognized by our system.");
            setIsTokenExpired(false);
            setIsValidLink(false);
          } else {
            setIsValidLink(true);
            setIsTokenExpired(false);
          }
        } else {
          setErrorMessage("Error validating registration link. Please try again later.");
          setIsValidLink(false);
          setIsTokenExpired(false);
        }
      } catch (error) {
        setErrorMessage("Error validating registration link. Please try again later.");
        setIsValidLink(false);
        setIsTokenExpired(false);
      } finally {
        setIsCheckingToken(false);
      }
    };

    checkToken();

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [token]);

  if (isCheckingToken) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: windowWidth >= 800 ? "50%" : "80%",
          height: "100vh",
        }}
      >
        <CircularProgress color="inherit" />
        <p style={{ color: "white", marginTop: "20px" }}>Validating registration link...</p>
      </div>
    );
  }

  if (isValidLink === false) {
    if (isTokenExpired) {
      return <ExpiredLinkMessage id={id} token={token} windowWidth={windowWidth} errorMessage={errorMessage} />;
    } else {
      return <InvalidLinkMessage windowWidth={windowWidth} errorMessage={errorMessage} />;
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: windowWidth >= 800 ? "50%" : "80%",
      }}
    >
      <div>
        <img
          src={
            s3BaseUrl + "/mobitutorza_logo_full.png"
          }
          alt={"MobiTutorZA Logo"}
          width={"250px"}
          height={"250px"}
        />
      </div>

      <UserAlreadyExistsBox
        display={showErrorBox}
        handler={() => setShowErrorBox(false)}
      />
      <SuccessfulRegistrationBox
        display={showSuccessBox}
        handler={() => {
          localStorage.setItem("logoutInitiated", "true"); // Set logout intent in localStorage
          localStorage.setItem("isLoggedIn", "0");
          history.push("/login");
        }}
      />

      <div>
        <p style={{ color: "white", fontSize: "1.25em", fontWeight: "bold" }}>
          Security
        </p>
        <hr style={{ color: "white", width: "100%", marginBottom: "20px" }} />
      </div>

      {isLoading ? (
        <CircularProgress color="inherit" />
      ) : (
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
          noValidate
          autoComplete="off"
        >
          <div>
            <p style={{ color: "#FBCD02" }}>
              Password must be at least 8 characters long and contain a mix of
              uppercase letters, lowercase letters, and numbers.
            </p>
          </div>
          <TextField
            sx={{
              backgroundColor: "rgba(224, 224, 224, 0.12)",
              borderRadius: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.875rem",
                color: "white",
              },
              "&.Mui-focused": {
                borderColor: "#00112e",
                "--joy-palette-primary-500": "#01ACC7",
              },
              width: "100%",
            }}
            InputProps={{
              sx: {
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FBCD02",
                },
              },
            }}
            InputLabelProps={{
              sx: {
                color: "#fbcd02",
                "&.Mui-focused": {
                  color: "#fbcd02",
                },
              },
            }}
            error={formErrors.username}
            helperText={formErrors.username ? "Username is required." : ""}
            label="Username"
            variant="outlined"
            onChange={(e) => handleInputChange("username", e.target.value)}
            onBlur={(e) => handleBlur("username", e.target.value)}
          />

          <TextField
            sx={{
              backgroundColor: "rgba(224, 224, 224, 0.12)",
              borderRadius: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.875rem",
                color: "white",
              },
              "&.Mui-focused": {
                borderColor: "#00112e",
                "--joy-palette-primary-500": "#01ACC7",
              },
              width: "100%",
            }}
            InputProps={{
              sx: {
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FBCD02",
                },
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    sx={{ color: "white" }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              sx: {
                color: "#fbcd02",
                "&.Mui-focused": {
                  color: "#fbcd02",
                },
              },
            }}
            error={formErrors.password}
            helperText={formErrors.password ? "Password is required." : ""}
            label="Password"
            variant="outlined"
            type={showPassword ? "text" : "password"}
            onChange={(e) => handleInputChange("password", e.target.value)}
            onBlur={(e) => handleBlur("password", e.target.value)}
          />

          <TextField
            sx={{
              backgroundColor: "rgba(224, 224, 224, 0.12)",
              borderRadius: "5px",
              "& .MuiInputBase-input": {
                padding: "15px",
                fontSize: "0.875rem",
                color: "white",
              },
              "&.Mui-focused": {
                borderColor: "#00112e",
                "--joy-palette-primary-500": "#01ACC7",
              },
              width: "100%",
            }}
            InputProps={{
              sx: {
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#FBCD02",
                },
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleToggleConfirmPasswordVisibility}
                    sx={{ color: "white" }}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              sx: {
                color: "#fbcd02",
                "&.Mui-focused": {
                  color: "#fbcd02",
                },
              },
            }}
            error={formErrors.confirmPassword}
            helperText={
              formErrors.confirmPassword === true && "Passwords do not match."
            }
            label="Confirm Password"
            variant="outlined"
            type={showConfirmPassword ? "text" : "password"}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            onBlur={(e) => handleBlur("confirmPassword", e.target.value)}
          />

          {/* {passwordCriteriaMessage && (
            <p className='PasswordValidationMessage'>
              Password must be at least 8 characters long and contain a mix of
              uppercase letters, lowercase letters, and numbers.
            </p>
          )} */}
          {/* {passwordMatchMessage && (
            <p className='PasswordValidationMessage'>Passwords must match.</p>
          )} */}

          <Button
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: "#FBCD02",
              color: "#020202",
              "&:hover": {
                backgroundColor: "#FBCD02",
              },
            }}
            onClick={handleSubmit}
          >
            Register
          </Button>
        </Box>
      )}
    </div>
  );
}
